import { Assert } from "../../Assert";
import { DataSource } from "../../Data/DataSource";
import { PartialNullable, RadPromiseLike } from "../../Extensions";
import { ArrayHelper } from "../../Helpers/ArrayHelper";
import { ObjectHelper } from "../../Helpers/ObjectHelper";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyString } from "../../RadObject";
import { ClientAwareCategoryAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareCategoryAttribute";
import { ClientAwareDisplayNameAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareDisplayNameAttribute";
import { WebApiClientService } from "../../Server/WebApiClientService";
import { ServiceManager } from "../../ServiceManager";
import { ComboBox, ComboBoxPropertyValues, IComboBox } from "./ComboBox";
import { ISoftDeleteEnabledListMixin, SoftDeleteEnabledListMixin } from "./Mixins/SoftDeleteEnabledListMixin";
import { NAMESPACE } from "./_Namespace";

export type ComboBoxApplicationEntityPropertyValues =
    ComboBoxPropertyValues
    & {
        extraProperties: string[],
        primaryEntity: string;
        primaryEntityDescriptionProperty: string;
        primaryEntityFilter: string;
        primaryEntityOrderProperty: string;
    };

export type ComboBoxApplicationEntityProperties = {
    [name in keyof ComboBoxApplicationEntityPropertyValues]: Property<ComboBoxApplicationEntityPropertyValues[name]>
};

export interface IComboBoxApplicationEntity extends IComboBox, ComboBoxApplicationEntityProperties, ISoftDeleteEnabledListMixin
{
}

export class ComboBoxApplicationEntity extends ComboBox implements IComboBoxApplicationEntity
{
    // #region Static

    public static override classNamespace = NAMESPACE + "ComboBoxApplicationEntity";
    public static override className = "ComboBoxApplicationEntity";

    // #endregion

    // #region Instance

    // #region Properties

    public extraProperties!: Property<string[]>;
    public primaryEntity!: PropertyString;
    public primaryEntityDescriptionProperty!: PropertyString;
    public primaryEntityFilter!: PropertyString;
    public primaryEntityOrderProperty!: PropertyString;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<ComboBoxApplicationEntityPropertyValues>)
    {
        super(propertyValues);
    }

    // #endregion

    // #region Public Methods

    public loadDataAsync(): Promise<void>
    {
        return new Promise((resolve, reject) =>
        {
            const
                dataSource = this.dataSource.get(),
                data = dataSource ? dataSource.data.get() : null,
                primaryEntity = this.primaryEntity.get(),
                primaryEntityDescriptionProperty = this.primaryEntityDescriptionProperty.get();

            let
                primaryEntityFilter = this.primaryEntityFilter.get(),
                primaryEntityOrderProperty = this.primaryEntityOrderProperty.get();

            Assert.isNotNullOrUndefined(primaryEntity, "primaryEntity");
            Assert.isNotNullOrUndefined(primaryEntityDescriptionProperty, "primaryEntityDescriptionProperty");

            if (!primaryEntityOrderProperty)
                primaryEntityOrderProperty = primaryEntityDescriptionProperty;

            Assert.isNotNullOrUndefined(primaryEntityOrderProperty, "primaryEntityOrderProperty");

            if (primaryEntityFilter)
            {
                primaryEntityFilter = primaryEntityFilter.replace(/\[([^\]]+)\]/g, (match, p1) =>
                {
                    const
                        value = data ? data[p1] : null;

                    if (ObjectHelper.isNullOrUndefined(value))
                        return "null";

                    return value.toString();
                });
            }

            const
                extraProperties = this.extraProperties.get() ?? [];

            ServiceManager.getRequired(WebApiClientService)
                .createEntitySearchRequest(
                    primaryEntity,
                    {
                        columns: ArrayHelper.distinct(extraProperties.concat(["id", primaryEntityDescriptionProperty])),
                        filter: primaryEntityFilter,
                        orderBy: primaryEntityOrderProperty,
                    }
                )
                .executeAsync()
                .then((data: any[] | undefined) =>
                {
                    data = (data ?? []) as any[];

                    const
                        dataSourceProperty = this.dataSourceProperty.get(),
                        dataSource = this.dataSource.get(),
                        requiredId =
                            dataSourceProperty && dataSource
                                ? dataSource.propertyValueByPathGet(dataSourceProperty)
                                : null;

                    if (requiredId === null || data.some(item => item.id === requiredId))
                    {
                        this.listDataSource.set(new DataSource({ data: this._createListDataSource(data, primaryEntityDescriptionProperty) }));
                        resolve(undefined);
                        return;
                    }

                    this
                        ._getSoftDeletedListItemAsync({
                            listData: data,
                            primaryEntity: primaryEntity,
                            primaryEntityDescriptionProperty: primaryEntityDescriptionProperty,
                            primaryEntityOrderProperty: (primaryEntityOrderProperty as string),
                            requiredId: requiredId
                        })
                        .finally(() =>
                        {
                            Assert.isNotNullOrUndefined(data, "data");

                            this.listDataSource.set(new DataSource({ data: data.map(item => ({ value: item.id, description: ObjectHelper.propertyValueByPathGet(item, primaryEntityDescriptionProperty), deleted: !!item.deletedGuid })) }));
                            resolve(undefined);
                        });
                })
                .catch(error =>
                {
                    reject(error);
                });
        });
    }

    // #endregion

    // #region Protected Methods
    protected _createListDataSource(data: any[], primaryEntityDescriptionProperty: string)
    {
        return data.map(item => ({ value: item.id, description: ObjectHelper.propertyValueByPathGet(item, primaryEntityDescriptionProperty) }));
    }
    protected _primaryEntityChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _primaryEntityDescriptionPropertyChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _primaryEntityFilterChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _primaryEntityOrderPropertyChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: ComboBoxApplicationEntityPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.extraProperties = new Property<string[]>(this, "System.Object, System.Private.CoreLib", propertyValues.extraProperties);
        this.primaryEntity = new PropertyString({
            context: this,
            initialValue: propertyValues.primaryEntity,
            changeHandler: this._primaryEntityChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Data" }),
                new ClientAwareDisplayNameAttribute({ displayName: "Primary Entity" }),
            ],
        });
        this.primaryEntityDescriptionProperty = new PropertyString({
            context: this,
            initialValue: propertyValues.primaryEntityDescriptionProperty,
            changeHandler: this._primaryEntityDescriptionPropertyChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Data" }),
                new ClientAwareDisplayNameAttribute({ displayName: "Primary Entity Description Property" }),
            ],
        });
        this.primaryEntityFilter = new PropertyString({
            context: this,
            initialValue: propertyValues.primaryEntityFilter,
            changeHandler: this._primaryEntityFilterChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Data" }),
                new ClientAwareDisplayNameAttribute({ displayName: "Primary Entity Filter" }),
            ],
        });
        this.primaryEntityOrderProperty = new PropertyString({
            context: this,
            initialValue: propertyValues.primaryEntityOrderProperty,
            changeHandler: this._primaryEntityOrderPropertyChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Data" }),
                new ClientAwareDisplayNameAttribute({ displayName: "Primary Entity Order Property" }),
            ],
        });
    }

    // #endregion

    // #region ISoftDeleteEnabledListMixin

    _getSoftDeletedListItemAsync(info: { listData: any[], primaryEntity: string, primaryEntityDescriptionProperty: string, primaryEntityOrderProperty: string, requiredId: string }): RadPromiseLike<any>
    {
        return SoftDeleteEnabledListMixin._getSoftDeletedListItemAsync(this, info);
    }

    // #endregion

    // #endregion
}

